import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Settings } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import SortableCut from "../../FsOptimizer/SortableCut";
import getShearingData from "../../../../helper/getOptimizedShearingInstructions";

const OptimizedFlats = ({
  flatSheetSize,
  pieces,
  flatsUsed,
  optimizerData,
  onOptimize,
}) => {
  const [open, setOpen] = useState(false);
  const [activeCut, setActiveCut] = useState(null);
  const [orderedCuts, setOrderedCuts] = useState(
    optimizerData ? optimizerData.orderedCuts : null
  );
  const [flatSheets, setFlatSheets] = useState(null); // The flatsheets are the Bins in the Bin Packing Algorithm
  const [flatSheetLowerBound, setFlatSheetLowerBound] = useState(0);
  const [totalDrop, setTotalDrop] = useState(0);
  const [fsUtilizationRate, setFsUtilizationRate] = useState(0);
  const [flatSheetsForShearing, setFlatSheetsForShearing] = useState(null);
  const [shearInstructions, setShearInstructions] = useState(null);
  const [shearingData, setShearingData] = useState(null);

  // Parse QuoteForm Pieces into Ordered Cuts
  const parsePiecesToOrderedCuts = (pieces) => {
    // Copy And Sort Pieces (Largest to Smallest)
    let orderedPieces = [];
    for (let piece of pieces) {
      orderedPieces.push({
        name: piece.name,
        quantity: piece.quantity,
        stretchOut: piece.stretchOut,
        tempId: uuidv4(),
      });
    }
    orderedPieces.sort((a, b) => {
      if (b.stretchOut - a.stretchOut != 0) {
        return b.stretchOut - a.stretchOut;
      } else {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      }
    });

    let cuts = [];
    let totalStretchOut = 0;
    for (let piece of orderedPieces) {
      for (let i = 0; i < piece.quantity; i++) {
        cuts.push({
          name: piece.name,
          stretchOut: piece.stretchOut,
          tempId: uuidv4(),
        });
        totalStretchOut += Number(piece.stretchOut);
      }
    }
    setFlatSheetLowerBound(Math.ceil(totalStretchOut / flatSheetSize));
    setOrderedCuts(cuts);

    parseSheetsByCuts(cuts, true);
  };

  // Only Call onOptimize if the user actually changes something
  const parseSheetsByCuts = (cuts, shouldUpdateData) => {
    if (cuts.length > 0) {
      let sheets = [{ pieces: [] }];
      // For Each Group
      for (let cut of cuts) {
        // Cuts are already in descending stretchout order
        let fsIndex = 0;

        // For Each Cut
        // Find which FS to add the Cut into
        let added = false;
        while (!added) {
          let currentFS = sheets[fsIndex];
          let currentFSUsedWidth = 0;
          for (let fsPiece of currentFS.pieces) {
            currentFSUsedWidth += Number(fsPiece.stretchOut);
          }

          let leftOverWidth = flatSheetSize - currentFSUsedWidth;
          if (leftOverWidth >= cut.stretchOut) {
            sheets[fsIndex].pieces = [
              ...currentFS.pieces,
              {
                name: cut.name,
                stretchOut: cut.stretchOut,
                tempId: uuidv4(),
              },
            ];
            added = true;
          } else {
            if (fsIndex >= sheets.length - 1) {
              sheets.push({
                pieces: [
                  {
                    name: cut.name,
                    stretchOut: cut.stretchOut,
                    tempId: uuidv4(),
                  },
                ],
              });
              added = true;
            }
            fsIndex++;
          }
        }
      }

      setFlatSheets(sheets);

      let totalInchesDrop = 0;
      let totalInchesUsed = 0;
      for (let i = 0; i < sheets.length; i++) {
        let flatSheet = sheets[i];
        let currentFSUsedWidth = 0;
        for (let fsPiece of flatSheet.pieces) {
          currentFSUsedWidth += Number(fsPiece.stretchOut);
        }
        let drop = flatSheetSize - currentFSUsedWidth;
        totalInchesDrop += drop;
        totalInchesUsed += currentFSUsedWidth;
      }
      setTotalDrop(totalInchesDrop);
      let utilization = parseInt(
        (100 * totalInchesUsed) / (totalInchesUsed + totalInchesDrop)
      );
      setFsUtilizationRate(utilization);

      if (shouldUpdateData) {
        // onOptimize(flatSheets.length, totalDrop, { orderedCuts });
        onOptimize(sheets.length, totalInchesDrop, { orderedCuts: cuts });
      }
    } else {
      setFlatSheets([]);
      setTotalDrop(0);
      setFsUtilizationRate(0);

      // onOptimize(flatSheets.length, totalDrop, { orderedCuts });
      onOptimize(0, 0, { orderedCuts: cuts });
      if (shouldUpdateData) {
        // onOptimize(flatSheets.length, totalDrop, { orderedCuts });
        onOptimize(0, 0, { orderedCuts: cuts });
      }
    }
    // Clear Out Shear Instructions
    // setShearInstructions([]);
    // setFlatSheetsForShearing([]);
    // setShearInstructions(null);
    setShearingData(null);
  };

  // Create Shearing Instructions From Cuts & FlatSheets
  // const reorganizeCutsAndFlatsForShearing = () => {
  //   console.log("reorganizeCutsAndFlatsForShearing", {
  //     pieces,
  //     orderedCuts,
  //     flatSheets,
  //   });
  //   // Copy Flat Sheets
  //   let sheetsCopy = [];
  //   for (let sheet of flatSheets) {
  //     let pieces = sheet.pieces;
  //     sheetsCopy.push({
  //       ...sheet,
  //       size: flatSheetSize,
  //       pieces: pieces.map((object) => ({ ...object })),
  //     });
  //   }
  //   console.log("sheetsCopy", sheetsCopy);

  //   // Generate Shear Instructions
  //   let instructions = [];

  //   // Sort Pieces in each flat sheet into descending size
  //   for (let sheet of sheetsCopy) {
  //     sheet.pieces.sort((a, b) => {
  //       if (b.stretchOut - a.stretchOut != 0) {
  //         return b.stretchOut - a.stretchOut;
  //       } else {
  //         if (a.name < b.name) {
  //           return -1;
  //         }
  //         if (a.name > b.name) {
  //           return 1;
  //         }
  //         return 0;
  //       }
  //     });
  //   }

  //   // Sort Flat Sheets By Cutting Order
  //   sheetsCopy.sort((a, b) => {
  //     let aPieces = a.pieces;
  //     let bPieces = b.pieces;

  //     let aIndex = 0;
  //     let bIndex = 0;
  //     while (aPieces[aIndex].stretchOut == bPieces[bIndex].stretchOut) {
  //       if (aIndex + 1 < aPieces.length) {
  //         aIndex++;
  //       } else {
  //         return -1;
  //       }
  //       if (bIndex + 1 < bPieces.length) {
  //         bIndex++;
  //       } else {
  //         return 1;
  //       }
  //     }

  //     return bPieces[bIndex].stretchOut - aPieces[aIndex].stretchOut;
  //   });
  //   setFlatSheetsForShearing(sheetsCopy);

  //   // Copy the sorted sheets (deep copy without references)

  //   let flatSheetsCopy = [];
  //   for (let sheet of sheetsCopy) {
  //     let pieces = sheet.pieces;
  //     flatSheetsCopy.push({
  //       ...sheet,
  //       size: Number(flatSheetSize),
  //       pieces: pieces.map((object) => ({ ...object })),
  //     });
  //   }

  //   // Copy Pieces and Order By Stretchout AND Name (if equal stretchout)
  //   let orderedPiecesCopy = [...pieces];
  //   orderedPiecesCopy.sort((a, b) => {
  //     if (Number(b.stretchOut) - Number(a.stretchOut) != 0) {
  //       return Number(b.stretchOut) - Number(a.stretchOut);
  //     } else {
  //       if (a.name < b.name) {
  //         return -1;
  //       }
  //       if (a.name > b.name) {
  //         return 1;
  //       }
  //       return 0;
  //     }
  //   });

  //   // Iterate Through Trim Types
  //   for (let orderedPiece of orderedPiecesCopy) {
  //     let currentPieceName = orderedPiece.name;
  //     let currentPieceStretchOut = orderedPiece.stretchOut;

  //     // Iterate Through Flat Sheets
  //     for (
  //       let sheetIndex = 0;
  //       sheetIndex < flatSheetsCopy.length;
  //       sheetIndex++
  //     ) {
  //       let currentSheet = flatSheetsCopy[sheetIndex];
  //       console.log("CURRENT SHEET", currentSheet);
  //       let currentSheetSizeBeforeCutting = currentSheet.size;

  //       let numberOfPiecesRemoved = 0;

  //       // Iterate Through Flat Sheet Cuts
  //       let allPiecesOfCurrentSizeRemovedFromSheet = false;
  //       let cutIndex = 0;
  //       while (
  //         cutIndex < currentSheet.pieces.length &&
  //         !allPiecesOfCurrentSizeRemovedFromSheet
  //       ) {
  //         let currentCut = currentSheet.pieces[cutIndex];
  //         console.log({
  //           currentCutName: currentCut.name,
  //           currentPieceName: currentPieceName,

  //           currentCutStretchOut: currentCut.stretchOut,
  //           currentPieceStretchOut: currentPieceStretchOut,
  //         });
  //         if (
  //           currentCut.name == currentPieceName &&
  //           Number(currentCut.stretchOut) == Number(currentPieceStretchOut)
  //         ) {
  //           console.log("CURRENT CUT MATCHES NAME AND STRETCHOUT");
  //           if (currentSheetSizeBeforeCutting < flatSheetSize) {
  //             console.log("ADD SHEAR INSTRUCTION");
  //             // + Add Instruction for Current Piece
  //             instructions.push(
  //               `[${currentSheetSizeBeforeCutting}" DROP - FS #${
  //                 sheetIndex + 1
  //               }] - SHEAR ${currentCut.stretchOut}" - ${currentCut.name}`
  //             );
  //           } else {
  //             console.log("DONT ADD SHEAR INSTRUCTION");
  //             // + Add Instruction for Current Piece
  //             instructions.push(
  //               `[FS #${sheetIndex + 1}] - SHEAR ${currentCut.stretchOut}" - ${
  //                 currentCut.name
  //               }`
  //             );
  //           }

  //           // Pop First Element
  //           numberOfPiecesRemoved++;

  //           // Reduce FlatSheet Size
  //           currentSheet.size =
  //             Number(currentSheet.size) - Number(currentCut.stretchOut);

  //           // Check If Next Piece Exists
  //           if (cutIndex + 1 < currentSheet.pieces.length) {
  //             let nextPiece = currentSheet.pieces[cutIndex + 1];
  //             // If next piece is the last piece and is equal to the current sheet size then it no longer needs cutting
  //             if (nextPiece.stretchOut == currentSheet.size) {
  //               instructions.push(
  //                 `[FS #${sheetIndex + 1}] - LAST PIECE ${
  //                   currentCut.stretchOut
  //                 }" - ${currentCut.name}`
  //               );
  //               // Pop This Element as well
  //               numberOfPiecesRemoved++;
  //               // Reduce FlatSheet Size
  //               currentSheet.size =
  //                 Number(currentSheet.size) - Number(nextPiece.stretchOut);
  //               cutIndex++;
  //             } else {
  //               // If there is last piece but is not equal to the current sheet size and not equal to the current cut size
  //               // then save the rest as drop for later
  //               if (nextPiece.stretchOut != currentCut.stretchOut) {
  //                 if (currentSheetSizeBeforeCutting < flatSheetSize) {
  //                   instructions.push(
  //                     `[${currentSheetSizeBeforeCutting}" DROP - FS #${
  //                       sheetIndex + 1
  //                     }] - SET ASIDE [${currentSheet.size}" DROP]`
  //                   );
  //                 } else {
  //                   instructions.push(
  //                     `[FS #${sheetIndex + 1}] - SET ASIDE [${
  //                       currentSheet.size
  //                     }" DROP]`
  //                   );
  //                 }
  //               }
  //             }
  //           } else {
  //             // If there are no more pieces but the sheet size > 0
  //             // Then the last piece is a drop
  //             if (currentSheet.size > 0) {
  //               {
  //                 if (currentSheetSizeBeforeCutting < flatSheetSize) {
  //                   instructions.push(
  //                     `[${currentSheetSizeBeforeCutting}" DROP - FS #${
  //                       sheetIndex + 1
  //                     }] - DROP ${currentSheet.size}" - put away for future use`
  //                   );
  //                 } else {
  //                   instructions.push(
  //                     `[FS #${sheetIndex + 1}] - DROP ${
  //                       currentSheet.size
  //                     }" - put away for future use`
  //                   );
  //                 }
  //               }
  //             }
  //           }
  //         } else {
  //           console.log("CURRENT CUT DOES NOT MATCH NAME AND STRETCHOUT");
  //           allPiecesOfCurrentSizeRemovedFromSheet = true;
  //         }
  //         cutIndex++;
  //       }
  //       // Remove Cuts that were removed from the array
  //       for (let i = 0; i < numberOfPiecesRemoved; i++) {
  //         currentSheet.pieces.shift();
  //       }
  //     }
  //   }
  //   setShearInstructions(instructions);
  // };

  const getShearingInstructions = () => {
    if (flatSheetSize && orderedCuts && pieces) {
      let sheardata = getShearingData(flatSheetSize, orderedCuts, pieces);
      setShearingData(sheardata);
    } else {
      setShearingData(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 120 }, // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragStart(event) {
    const { active } = event;
    const currentActive = orderedCuts.find((cut) => cut.tempId == active.id);
    setActiveCut(currentActive);
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      let cutsCopy = [...orderedCuts];
      const oldIndex = cutsCopy.findIndex((cut) => cut.tempId == active.id);
      const newIndex = cutsCopy.findIndex((cut) => cut.tempId == over.id);
      let newCutOrder = arrayMove(cutsCopy, oldIndex, newIndex);

      // Update OrderedCuts and re-parse Flat SHeets
      setOrderedCuts(newCutOrder);
      parseSheetsByCuts(newCutOrder, true);
    }

    setActiveCut(null);
  }

  // Initial Load should skip parsing pieces if the optimizerData exists and just set the optimizerData.orderedCuts as orderedCuts
  // Differentiate initial load with existing data vs no existing data
  useEffect(() => {
    if (pieces && pieces.length > 0) {
      // If optimizer data exists but flatsheets aren't defined, then it is the initial load with existing data
      if (!flatSheets && optimizerData) {
        setOrderedCuts(optimizerData.orderedCuts); // Set ordered cuts with existing data
        parseSheetsByCuts(optimizerData.orderedCuts, false); // Parse sheets without updating data
      } else {
        // For every other case,we want to recalculate everything
        // - quantity change
        // - stretchout change
        // - piece added
        // - piece removed
        parsePiecesToOrderedCuts(pieces);
      }
    } else {
      setOrderedCuts([]);
      // setFlatSheets([]);
    }
  }, [pieces, flatSheetSize]);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <TextField
          sx={{ width: "120px" }}
          size="small"
          label="Optimized Flats"
          type="number"
          value={flatsUsed}
          InputProps={{
            readOnly: true,
            disableUnderline: true,
            endAdornment: (
              <InputAdornment>
                <IconButton>
                  <Settings
                    onClick={() => {
                      setOpen(true);
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          disabled
          sx={{ width: "120px", marginLeft: "8px" }}
          size="small"
          label="FS Utilization"
          type="number"
          value={fsUtilizationRate}
          InputProps={{
            readOnly: true,
            disableUnderline: true,
            endAdornment: "%",
          }}
        />
      </Box>
      <Dialog
        fullScreen={true}
        fullWidth={true}
        open={open}
        onClose={() => {
          setOpen(false);
          setShearingData(null);
        }}
      >
        <DialogTitle>FS Optimizer</DialogTitle>
        <DialogContent>
          {open && (
            <Box sx={{ height: "100%", margin: 0 }}>
              <Typography variant="h6" sx={{ marginTop: "32px" }}>
                Flat Sheet Size: {flatSheetSize} in.
              </Typography>

              {/* Sort Cut Priority */}
              <Box>
                <Box
                  sx={{
                    marginTop: "32px",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Typography variant="h6" component="div">
                    Change Cut Priority
                  </Typography>
                  <Button
                    onClick={() => {
                      parsePiecesToOrderedCuts(pieces);
                    }}
                  >
                    Reset
                  </Button>
                </Box>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  {orderedCuts && (
                    <SortableContext
                      items={orderedCuts}
                      strategy={rectSortingStrategy}
                    >
                      <Grid container sx={{ marginTop: "16px" }}>
                        {orderedCuts.map((orderedCut, index) => {
                          return (
                            <SortableCut
                              key={orderedCut.tempId}
                              orderedCut={orderedCut}
                              showLeftAndRightButtons={true}
                              disableLeft={!(index > 0)}
                              onClickLeft={() => {
                                let orderedCutsCopy = [...orderedCuts];
                                let prev = orderedCutsCopy[index - 1];
                                let current = orderedCutsCopy[index];
                                orderedCutsCopy[index - 1] = current;
                                orderedCutsCopy[index] = prev;
                                setOrderedCuts(orderedCutsCopy);
                              }}
                              disableRight={!(index < orderedCuts.length - 1)}
                              onClickRight={() => {
                                let orderedCutsCopy = [...orderedCuts];
                                let current = orderedCutsCopy[index];
                                let next = orderedCutsCopy[index + 1];
                                orderedCutsCopy[index + 1] = current;
                                orderedCutsCopy[index] = next;
                                setOrderedCuts(orderedCutsCopy);
                              }}
                            />
                          );
                        })}
                      </Grid>
                    </SortableContext>
                  )}

                  <DragOverlay>
                    {activeCut ? <SortableCut orderedCut={activeCut} /> : null}
                  </DragOverlay>
                </DndContext>
              </Box>

              <Typography
                variant="h6"
                component="div"
                sx={{ marginTop: "32px" }}
              >
                Flat Sheets: {flatSheets ? flatSheets.length : 0} (Lowerbound ={" "}
                {flatSheetLowerBound} FS). Total In. Drop = {totalDrop}"
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="caption">
                  The lowerbound is the minimum number of flatsheets required to
                  fit the combined stretchouts assuming there are no drops.
                  Lowerbound = CEILING(total stretchouts / flat sheet size)
                </Typography>
                <Typography variant="caption">
                  There's no guarantee that the best solution will equal the
                  lowerbound but it is a good way to see if a good solution is
                  given. If the total FS is equal to the lowerbound, then we
                  know that the total flatsheets used cannot be reduced further
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{}}>
                {flatSheets &&
                  flatSheets.map((flatSheet, index) => {
                    let currentFSUsedWidth = 0;
                    for (let fsPiece of flatSheet.pieces) {
                      currentFSUsedWidth += Number(fsPiece.stretchOut);
                    }
                    let drop = flatSheetSize - currentFSUsedWidth;
                    return (
                      <Grid
                        key={flatSheet.tempId}
                        item
                        sx={{
                          marginTop: "16px",
                          border: null,
                          background: "inherit",
                          width: "120px",
                        }}
                      >
                        <Typography>FS #{index + 1}</Typography>
                        <Box sx={{ width: "100%" }}>
                          {flatSheet.pieces.map((piece, index) => {
                            return (
                              <Box
                                key={piece.tempId}
                                sx={{
                                  borderTop: 1,
                                  borderBottom: 1,
                                  // borderLeft: 1,
                                  // borderLeftStyle: "dotted",
                                  // borderRight: 1,
                                  // borderRightStyle: "dotted",
                                  // The times 6.25 is so that the box will have a height of approx 300px (usually the mc width is 48 inches)
                                  // 300px / 48 inches = 6.25px/in
                                  height: `${piece.stretchOut * 6.25}px`,
                                  minHeight: "25px",
                                  width: `100%`,
                                  background: "green",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    paddingLeft: "4px",
                                    paddingRight: "4px",
                                    width: "100%",
                                    textAlign: "center",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: "1",
                                    WebkitBoxOrient: "vertical",
                                  }}
                                  inline
                                >
                                  {piece.stretchOut}" - {piece.name}
                                </Typography>
                              </Box>
                            );
                          })}
                          {drop > 0 && (
                            <Box
                              sx={{
                                borderTop: 1,
                                borderBottom: 1,
                                // borderLeft: 1,
                                // borderLeftStyle: "dotted",
                                // borderRight: 1,
                                // borderRightStyle: "dotted",
                                // The times 6.25 is so that the box will have a height of approx 300px (usually the mc width is 48 inches)
                                // 300px / 48 inches = 6.25px/in
                                height: `${drop * 6.25}px`,
                                minHeight: "25px",
                                width: `100%`,
                                background: "gray",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography sx={{ fontSize: "12px" }}>
                                {drop}" Drop
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
              </Grid>

              <Button
                sx={{ marginTop: "16px" }}
                onClick={() => {
                  getShearingInstructions();
                }}
              >
                Reorganize Cuts and Generate Shearing Instructions
              </Button>

              {shearingData && (
                <Box>
                  {shearingData.shearingInstructions &&
                    shearingData.shearingInstructions.length > 0 && (
                      <Box>
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{ marginTop: "32px" }}
                        >
                          Shearing Instructions
                        </Typography>
                        <Box sx={{}}>
                          <Grid container spacing={2} sx={{}}>
                            {shearingData.flatSheetsForShearing.map(
                              (flatSheet, index) => {
                                let { title, count, sheetTemplate } = flatSheet;
                                let currentFSUsedWidth = 0;
                                for (let fsPiece of sheetTemplate.pieces) {
                                  currentFSUsedWidth += Number(
                                    fsPiece.stretchOut
                                  );
                                }
                                let drop = flatSheetSize - currentFSUsedWidth;
                                return (
                                  <Grid
                                    key={sheetTemplate.tempId}
                                    item
                                    sx={{
                                      marginTop: "16px",
                                      border: null,
                                      background: "inherit",
                                      // width: "120px",
                                    }}
                                    xs={4}
                                    sm={3}
                                    md={2}
                                  >
                                    <Typography>{title}</Typography>
                                    <Box sx={{ width: "100%" }}>
                                      {sheetTemplate.pieces.map(
                                        (piece, index) => {
                                          return (
                                            <Box
                                              key={piece.tempId}
                                              sx={{
                                                borderTop: 1,
                                                borderBottom: 1,
                                                // borderLeft: 1,
                                                // borderLeftStyle: "dotted",
                                                // borderRight: 1,
                                                // borderRightStyle: "dotted",
                                                // The times 6.25 is so that the box will have a height of approx 300px (usually the mc width is 48 inches)
                                                // 300px / 48 inches = 6.25px/in
                                                height: `${
                                                  piece.stretchOut * 6.25
                                                }px`,
                                                minHeight: "25px",
                                                width: "100%",
                                                background: "green",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontSize: "12px",
                                                  paddingLeft: "4px",
                                                  paddingRight: "4px",
                                                  width: "100%",
                                                  textAlign: "center",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  display: "-webkit-box",
                                                  WebkitLineClamp: "1",
                                                  WebkitBoxOrient: "vertical",
                                                }}
                                                inline
                                              >
                                                {piece.stretchOut}" -{" "}
                                                {piece.name}
                                              </Typography>
                                            </Box>
                                          );
                                        }
                                      )}
                                      {drop > 0 && (
                                        <Box
                                          sx={{
                                            borderTop: 1,
                                            borderBottom: 1,
                                            // borderLeft: 1,
                                            // borderLeftStyle: "dotted",
                                            // borderRight: 1,
                                            // borderRightStyle: "dotted",
                                            // The times 6.25 is so that the box will have a height of approx 300px (usually the mc width is 48 inches)
                                            // 300px / 48 inches = 6.25px/in
                                            height: `${drop * 6.25}px`,
                                            minHeight: "25px",
                                            width: `100%`,
                                            background: "gray",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <Typography sx={{ fontSize: "12px" }}>
                                            {drop}" Drop
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Grid>
                                );
                              }
                            )}
                          </Grid>
                          <Box sx={{ marginTop: "16px" }}>
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              {shearingData.shearingInstructions.map(
                                (instruction, index) => {
                                  return (
                                    <Typography key={index} variant="caption">
                                      {index + 1}: {instruction}
                                    </Typography>
                                  );
                                }
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setShearingData(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OptimizedFlats;
